import API from '@aws-amplify/api';
import { Link } from 'react-router-dom';
import React, { useContext, useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import Button from '../shared/Button';
import DateTimeText from '../shared/DateTimeText';
import E164 from '../shared/E164';
import Grid from '../shared/Grid';
import { ProtectedRouteContext } from '../Context';
import { Tab, TabContainer } from '../shared/TabControl';
import UserType from '../shared/UserType';
import { userTypes } from '../../utils/enums';
import ManualNotificationDashboard from './manualNotificationDashboard';
import ExportCognito from '../shared/exportCognito';

import './Settings.scss';

function Settings() {
    const { userSettings } = useContext(ProtectedRouteContext);
    const [chosenClient, setChosenClient] = useState(userSettings.ClientOrgId);
    const [clientsLoading, setClientsLoading] = useState(true);
    const [clientsList, setClientsList] = useState([]);
    const [usersLoading, setUsersLoading] = useState(true);
    const [usersList, setUsersList] = useState([]);
    const [t] = useTranslation();
    const history = useHistory();

    const userHeaders = [
        {
            colLabel: t('Settings.Users.Name'),
            colName: 'Name',
            sortFunc: (dir) => {
                if (!dir) {
                    return (a, b) => {
                        let aName = `${a['FirstName']} ${a['LastName']}`;
                        let bName = `${b['FirstName']} ${b['LastName']}`;
                        return aName.localeCompare(bName);
                    };
                } else {
                    return (a, b) => {
                        let aName = `${a['FirstName']} ${a['LastName']}`;
                        let bName = `${b['FirstName']} ${b['LastName']}`;
                        return bName.localeCompare(aName);
                    };
                }
            },
            filterOn: false,
        },
        {
            colLabel: t('Settings.Users.Phone'),
            colName: 'PhoneNumber',
            sortFunc: (dir) => {
                if (!dir) {
                    return (a, b) => {
                        return a['Phone'].localeCompare(b['Phone']);
                    };
                } else {
                    return (a, b) => {
                        return b['Phone'].localeCompare(a['Phone']);
                    };
                }
            },
        },
        {
            colLabel: t('Settings.Users.Email'),
            colName: 'EmailAddress',
            sortFunc: (dir) => {
                if (!dir) {
                    return (a, b) => {
                        return a['Email'].localeCompare(b['Email']);
                    };
                } else {
                    return (a, b) => {
                        return b['Email'].localeCompare(a['Email']);
                    };
                }
            },
        },
        {
            colLabel: t('Settings.Users.Type'),
            colName: 'UserType',
            sortFunc: (dir) => {
                if (!dir) {
                    return (a, b) => {
                        return a['UserTypeID'] - b['UserTypeID'];
                    };
                } else {
                    return (a, b) => {
                        return b['UserTypeID'] - a['UserTypeID'];
                    };
                }
            },
        },
        {
            colLabel: t('Settings.Users.Updated'),
            colName: 'LastUpdated',
            sortFunc: (dir) => {
                if (!dir) {
                    return (a, b) => {
                        return new Date(a['LastUpdatedUTC']) - new Date(b['LastUpdatedUTC']);
                    };
                } else {
                    return (a, b) => {
                        return new Date(b['LastUpdatedUTC']) - new Date(a['LastUpdatedUTC']);
                    };
                }
            },
        },
    ];

    const handleChosenClientChange = async (event) => {
        const newChosenId = parseInt(event.target.value);
        setChosenClient(newChosenId);
    };

    const handleCreateUser = async () => {
        history.push('/sitesettings/user/0?1');
    };

    useEffect(() => {
        (async () => {
            const addColumns = (a) => {
                let fullName = `${a.FirstName} ${a.LastName}`.trim();
                fullName = fullName || `User ${a.UserID}`;

                let columns = [
                    <span colname="Name">
                        <span className={a.IsActive ? '' : 'inactive'}>
                            <Link to={`/sitesettings/user/${a.UserID}?1`}>{fullName}</Link>
                        </span>
                    </span>,
                    <span colname="PhoneNumber">
                        <E164 phoneNumber={a.Phone} />
                    </span>,
                    <span colname="EmailAddress">{a.Email}</span>,
                    <span colname="UserType">
                        <UserType userType={a.UserTypeID} />
                    </span>,
                    <span colname="LastUpdated">
                        <DateTimeText
                            date={a.LastUpdatedUTC}
                            type="short-time"
                        />
                    </span>,
                ];
                return Object.assign({}, a, { columns: columns });
            };

            let response = await API.get('ShipyardAPI', '/sitesettings/' + chosenClient);

            let gridRows = response.users.map(addColumns);

            setClientsList(response.clients);
            setUsersList({
                rows: gridRows,
            });
            setClientsLoading(false);
            setUsersLoading(false);
        })();
    }, [chosenClient]);

    return (
        <div className="settings-component">
            <div className="header">
                <h2>
                    <ReactMarkdown>{t('Settings.Title')}</ReactMarkdown>
                </h2>
                <div
                    className="client-picker"
                    hidden={clientsList.length < 2}
                >
                    <label htmlFor="client-picker">Site</label>
                    <select
                        id="client-picker"
                        disabled={clientsLoading}
                        value={chosenClient}
                        onChange={handleChosenClientChange}
                    >
                        {clientsList.map((client) => {
                            return (
                                <option
                                    key={`client-opt-${client.ClientOrgID}`}
                                    value={client.ClientOrgID}
                                >
                                    {client.OrgName}
                                </option>
                            );
                        })}
                    </select>
                </div>
            </div>
            <TabContainer>
                {/* #TODO Uncomment this when implementation is ready to begin. */}
                {/* <Tab title={t("Settings.Tab.Organization")}>
                    TODO: My Organization
                </Tab> */}
                <Tab title={t('Settings.Tab.Users')}>
                    {userSettings.UserType !== userTypes.Admin && userSettings.UserType !== userTypes.ScholarshipOrg ? null : (
                        <div className="buttons">
                            <Button
                                type="primary"
                                disabled={usersLoading}
                                onClick={handleCreateUser}
                                interactive={false}
                            >
                                Add User
                            </Button>
                            {userSettings.UserType === 4 ? <ExportCognito loading={usersLoading} /> : null}
                        </div>
                    )}
                    {usersLoading ? null : (
                        <Grid
                            name="users-grid"
                            className="users-grid-component"
                            counter={false}
                            data={usersList}
                            filters={false}
                            headers={userHeaders}
                            pageSize={20}
                            search={true}
                        />
                    )}
                </Tab>

                {userSettings?.UserType >= 4 ? (
                    <Tab title="Notifications">
                        <ManualNotificationDashboard />
                    </Tab>
                ) : null}
                {/* #TODO Uncomment this when implementation is ready to begin. */}
                {/* <Tab title={t("Settings.Tab.Schools")} disabled={true}>
                    TODO: Private Schools
                </Tab> */}
            </TabContainer>
        </div>
    );
}

export default Settings;
